import React, { useEffect, useMemo, useState } from 'react';
import { assignBoxLocation } from '../api';
import { clearPendingLocation, savePendingLocation } from '../locationFlow';

export default function LocationAssign({
  baseCode,
  initialLevel,
  initialBoxNumber,
  boxes,
  onAssigned,
  onClose,
}) {
  const [level, setLevel] = useState(initialLevel || 1);
  const [boxNumber, setBoxNumber] = useState(initialBoxNumber || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedBox, setSavedBox] = useState(null);

  const fullLocation = `${baseCode}-E${level}`;
  const normalizedBoxNumber = boxNumber.trim().toUpperCase();
  const targetBox = useMemo(
    () => boxes.find(box => box.box_nummer.toUpperCase() === normalizedBoxNumber),
    [boxes, normalizedBoxNumber]
  );
  const occupiedBy = boxes.find(
    box => box.standort === fullLocation && box.id !== targetBox?.id
  );

  useEffect(() => {
    savePendingLocation(baseCode, level);
  }, [baseCode, level]);

  async function handleAssign(event) {
    event.preventDefault();
    if (!normalizedBoxNumber) {
      setError('Bitte jetzt den Box-QR scannen oder eine Boxnummer eingeben.');
      return;
    }
    if (occupiedBy) {
      setError(`${fullLocation} ist bereits durch Box ${occupiedBy.box_nummer} belegt.`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const updated = await assignBoxLocation(normalizedBoxNumber, fullLocation);
      clearPendingLocation();
      setSavedBox(updated);
      onAssigned(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    clearPendingLocation();
    onClose();
  }

  return (
    <div className="box-form-overlay location-assign-overlay">
      <div className="location-assign-modal">
        <button type="button" className="location-assign-close" onClick={handleClose} aria-label="Schließen">×</button>

        {savedBox ? (
          <div className="location-assign-success">
            <div className="location-assign-success-mark">✓</div>
            <div className="location-assign-kicker">Eingelagert</div>
            <h2>{savedBox.box_nummer}</h2>
            <div className="location-assign-code">{savedBox.standort}</div>
            <p>Die Box ist jetzt unter diesem Dachbodenplatz auffindbar.</p>
            <button type="button" className="app-button app-button-primary" onClick={handleClose}>Fertig</button>
          </div>
        ) : (
          <>
            <div className="location-assign-kicker">Stellfeld erkannt</div>
            <h2 className="location-assign-title">{baseCode}</h2>
            <p className="location-assign-intro">
              Wähle die Stapelhöhe und scanne danach den QR-Code der Box.
            </p>

            <form onSubmit={handleAssign}>
              <label className="location-assign-label">
                Ebene im Stapel
                <select value={level} onChange={event => setLevel(Number(event.target.value))}>
                  {Array.from({ length: 9 }, (_, index) => index + 1).map(value => (
                    <option value={value} key={value}>E{value}{value === 1 ? ' · unterste Box' : ''}</option>
                  ))}
                </select>
              </label>

              <div className="location-assign-target">
                Gewählter Platz <strong>{fullLocation}</strong>
              </div>

              <label className="location-assign-label">
                Box scannen oder eingeben
                <input
                  autoFocus={!!initialBoxNumber}
                  value={boxNumber}
                  onChange={event => setBoxNumber(event.target.value)}
                  placeholder="zum Beispiel B012"
                  autoCapitalize="characters"
                />
              </label>

              {!initialBoxNumber && (
                <div className="location-assign-scan-note">
                  Öffne jetzt die Handykamera und scanne das Etikett der Box. Der gewählte Platz bleibt zwei Stunden gespeichert.
                </div>
              )}

              {targetBox && (
                <div className="location-assign-box-preview">
                  <strong>{targetBox.box_nummer}</strong>
                  <span>{targetBox.kategorie || targetBox.inhalt_stichworte || 'Vorbereitete Box'}</span>
                  {targetBox.standort && <small>Bisher: {targetBox.standort}</small>}
                </div>
              )}
              {!targetBox && normalizedBoxNumber && (
                <div className="location-assign-box-preview">
                  <strong>{normalizedBoxNumber}</strong>
                  <span>Neue, noch nicht erfasste Box</span>
                </div>
              )}
              {occupiedBy && (
                <div className="location-assign-warning">Belegt durch {occupiedBy.box_nummer}</div>
              )}
              {error && <div className="app-error location-assign-error">{error}</div>}

              <button
                type="submit"
                className="app-button app-button-primary location-assign-submit"
                disabled={saving || !!occupiedBy}
              >
                {saving ? 'Wird zugeordnet…' : `${normalizedBoxNumber || 'Box'} hier einlagern`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
